import html2canvas from "html2canvas";
import { toast } from "react-toastify";
import { getAuth } from "./auth-helper";
import { PASS_VIEW } from "enums";
import { Environment } from "enums";



function vcfText({
  prefix,
  firstName,
  lastName,
  organizationName,
  email,
  phones,
  jobTitle,
  photo,
  companyURL,
  socials,
  addresses,
  personalAccount,
}) {
  const safe = (v) => (v === null || v === undefined ? "" : String(v).trim());

  const getFullName = () => {
    const p = safe(prefix);
    const f = safe(firstName);
    const l = safe(lastName);
    return [p, f, l].filter(Boolean).join(" ");
  };

  const getName = () => {
    const p = safe(prefix);
    const f = safe(firstName);
    const l = safe(lastName);

    // N:Last;First;;Prefix;
    return `${l || ""};${f || ""};;${p || ""};`;
  };

  let str = `BEGIN:VCARD
VERSION:3.0`;

  // ✅ ORG
  if (safe(organizationName)) str += `\nORG:${safe(organizationName)}`;

  // ✅ FN + N
  const fullName = getFullName();
  if (fullName) str += `\nFN:${fullName}`;
  str += `\nN:${getName()}`;

  // ✅ TITLE
  if (safe(jobTitle)) str += `\nTITLE:${safe(jobTitle).toUpperCase()}`;

  // ✅ EMAIL
  // if (safe(email)) str += `\nEMAIL;TYPE=INTERNET:${safe(email)}`;
  if (safe(email)) str += `\nEMAIL:${safe(email)}`;


  // ✅ PHOTO (only if photo exists)
  if (safe(photo)) str += `\nPHOTO;VALUE=URL;TYPE=JPEG:${safe(photo)}`;

  // ✅ COMPANY URLS (supports old + new typed format)
  if (Array.isArray(companyURL)) {
    companyURL.forEach((item) => {
      if (!item) return;

      // OLD: ["https://x-age.ai"]
      if (typeof item === "string") {
        const url = safe(item);
        if (url) str += `\nURL:${url}`;
        return;
      }

      // NEW: [{ type:"work", value:"https://x-age.ai" }]
      const type = safe(item?.type).toLowerCase();
      const value = safe(item?.value);
      if (!value) return;

      if (["work", "home", "custom"].includes(type)) {
        str += `\nURL;TYPE=${type}:${value}`;
      } else {
        str += `\nURL:${value}`;
      }
    });
  } else if (safe(companyURL)) {
    // OLD single string
    str += `\nURL:${safe(companyURL)}`;
  }

  // ✅ SOCIALS (type-based + fallback for old users)
  if (Array.isArray(socials)) {
    socials.forEach((item) => {
      const value = safe(item?.value);
      if (!value) return;

      const type = safe(item?.type).toLowerCase();
      const lower = value.toLowerCase();

      // 1) NEW UI: type exists
      if (type) {
        // URL types
        if (["work", "home", "custom"].includes(type)) {
          str += `\nURL;TYPE=${type}:${value}`;
          return;
        }

        // social types
        if (["linkedin", "facebook", "twitter", "x"].includes(type)) {
          const finalType = type === "x" ? "twitter" : type;
          str += `\nX-SOCIALPROFILE;TYPE=${finalType}:${value}`;
          return;
        }

        // unknown type -> treat as URL
        if (lower.startsWith("http")) str += `\nURL:${value}`;
        return;
      }

      // 2) OLD USERS fallback: infer from URL
      if (lower.includes("linkedin.com")) {
        str += `\nX-SOCIALPROFILE;TYPE=linkedin:${value}`;
      } else if (lower.includes("facebook.com")) {
        str += `\nX-SOCIALPROFILE;TYPE=facebook:${value}`;
      } else if (lower.includes("twitter.com") || lower.includes("x.com")) {
        str += `\nX-SOCIALPROFILE;TYPE=twitter:${value}`;
      } else if (lower.startsWith("http")) {
        str += `\nURL:${value}`;
      }
    });
  }

  // ✅ personalAccount (no hardcode)
  if (personalAccount) {
    const savedViaUrl =
      process.env.REACT_APP_SAVED_VIA_URL || "https://www.cardlysa.com";
    const savedViaLabel =
      process.env.REACT_APP_SAVED_VIA_LABEL || "Saved via Cardly";
    str += `\nURL;TYPE=${savedViaLabel}:${savedViaUrl}`;
  }

  // ✅ Addresses (skip if empty)
  if (Array.isArray(addresses)) {
    addresses.forEach(({ type, value }) => {
      const adrValue = safe(value);
      if (!adrValue) return;
      str += `\nADR;TYPE=${safe(type).toUpperCase() || "WORK"}:;;${adrValue}`;
    });
  }

    const normalizePhoneType = (t) => {
    const type = safe(t).toLowerCase();
    if (type === "mobile" || type === "cell" || type === "primary") return "CELL";
    if (type === "home") return "HOME";
    if (type === "work") return "WORK";
    return "WORK";
  };


  // ✅ Phones (skip invalid)
  if (Array.isArray(phones)) {
    phones.forEach(({ type, dialCode, value }) => {
      const d = safe(dialCode);
      const v = safe(value);
      if (!v) return;
      const phoneFinal = `${d}${v}`;
      if (phoneFinal.toLowerCase().includes("undefined")) return;
      // str += `\nTEL;TYPE=${safe(type).toUpperCase() || "WORK"}:${phoneFinal}`;
      const phoneType = normalizePhoneType(type);
str += `\nTEL;TYPE=${phoneType}:${phoneFinal}`;

    });
  }

  str += `\nEND:VCARD`;
  // return str;
  return str.replace(/\n/g, "\r\n");
}




function getLuminance(color) {
  const r = parseInt(color.slice(1, 3), 16);
  const g = parseInt(color.slice(3, 5), 16);
  const b = parseInt(color.slice(5, 7), 16);

  return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
}

// Function to determine the contrast ratio between two colors
function getContrastRatio(color1, color2) {
  const luminance1 = getLuminance(color1);
  const luminance2 = getLuminance(color2);

  const brighter = Math.max(luminance1, luminance2);
  const darker = Math.min(luminance1, luminance2);

  return brighter / (darker + 3);
}

// Function to invert text color based on background color
function invertTextColor(background) {
  const textColor =
    getContrastRatio(background, "#000000") >
      getContrastRatio(background, "#FFFFFF")
      ? "#000000"
      : "#FFFFFF";
  return textColor;
}

function isDark(background) {
  return (
    getContrastRatio(background, "#000000") >
    getContrastRatio(background, "#FFFFFF")
  );
}

const showToastMessage = (message) => {
  toast(message, {
    position: toast.POSITION.TOP_CENTER,
    className: "toast-message",
  });
};

const formateDate = (date) => {
  return new Date(date).toLocaleString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const isEmptyJSON = (json) => JSON.stringify(json) === "{}";

const getFile = (dataURL) => {
  const base64Data = dataURL.split(",")[1];
  const mimeType = "image/png"; // Adjust MIME type as needed
  const byteCharacters = atob(base64Data);
  const byteArrays = [];
  for (let i = 0; i < byteCharacters.length; i++) {
    byteArrays.push(byteCharacters.charCodeAt(i));
  }
  const blob = new Blob([new Uint8Array(byteArrays)], { type: mimeType });

  const fileName = "croppedImage.png";
  const file = new File([blob], fileName, { type: mimeType });

  return file;
};

const currencyFormat = (amount, discount = false) => {
  const currencyFormatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "SAR",
    minimumFractionDigits: 2,
  });

  // Format the amount using the currency formatter
  const formattedAmount = currencyFormatter.format(amount);

  // Apply minus sign if discount is true
  return discount ? `SAR -${formattedAmount.slice(3)}` : formattedAmount;
};

const handleConvertToPNG = async (svg) => {
  const element = document.getElementById(svg);

  if (!element) return;

  const canvas = await html2canvas(element, {
    backgroundColor: "transparent",
    useCORS: true,
  });
  const image = new Promise((resolve) => {
    canvas.toBlob((blob) => {
      const reader = new FileReader();
      reader.onload = () => {
        const dataUrl = reader.result;
        resolve(dataUrl);
      };
      reader.readAsDataURL(blob);
    });
  });
  return image;
};

const getPassURL = (passType) => {
  const queryParams = new URLSearchParams(window.location.search);
  const token = getAuth().token || queryParams.get("token");

  if (passType == PASS_VIEW.ANDROID)
    return process.env.REACT_APP_ENV === Environment.STAGING
      ? "https://api.cardlysa.net/api/v1/pass/google?token=" + token
      : "https://api.cardlysa.com/api/v1/pass/google?token=" + token;

  return process.env.REACT_APP_ENV == Environment.STAGING
    ? "https://app.cardlysa.net/generate-pass?token=" + token
    : "https://app.cardlysa.com/generate-pass?token=" + token;
};

export {
  vcfText,
  invertTextColor,
  isDark,
  showToastMessage,
  isEmptyJSON,
  formateDate,
  getFile,
  currencyFormat,
  handleConvertToPNG,
  getPassURL,
};
